import { appendQuery } from './append-query';
import { substitutePathParams } from './generate-path';

// Design Studio's editor URLs. They are TanStack routes now, but these helpers build them without
// importing the router, so the shared modules that link to the editor stay light. The output matches
// the manual `generatePath` they replace byte for byte (params are not URL-encoded).
const EDITOR_PATHS = {
  email: '/workspaces/:workspaceId/design-studio/editor/:id',
  whatsapp: '/workspaces/:workspaceId/design-studio/editor/whatsapp/:id',
  sms: '/workspaces/:workspaceId/design-studio/editor/sms/:id',
  push: '/workspaces/:workspaceId/design-studio/editor/push/:id',
  'inbox-message': '/workspaces/:workspaceId/design-studio/editor/inbox-message/:id',
  'sign-up-form': '/workspaces/:workspaceId/design-studio/editor/sign-up-form/:id',
  'in-app': '/workspaces/:workspaceId/design-studio/editor/in-app/:id',
} as const;

export type DesignStudioEditorKind = keyof typeof EDITOR_PATHS;

type EditorParams = { workspaceId: string | number; id: string | number };
type Query = Record<string, string | number | boolean | undefined | null>;

/** The Design Studio editor URL for a node of `kind`, with an optional query. */
export function designStudioEditorUrl(kind: DesignStudioEditorKind, params: EditorParams, query?: Query): string {
  return appendQuery(substitutePathParams(EDITOR_PATHS[kind], params), query);
}

/** The Design Studio dashboard for a workspace. */
export function designStudioDashboardUrl(workspaceId: string | number): string {
  return substitutePathParams('/workspaces/:workspaceId/design-studio/dashboard', { workspaceId });
}

/**
 * Where `/design-studio/editor` leads: `?file=<id>` opens that file (keeping the other query params,
 * like `tour`, exactly as written); without it, the dashboard.
 */
export function designStudioEditorIndexTarget(workspaceId: string | number, searchStr: string): string {
  const search = new URLSearchParams(searchStr);
  const file = search.get('file');
  if (file === null || file === '') {
    return designStudioDashboardUrl(workspaceId);
  }

  search.delete('file');
  return appendQuery(designStudioEditorUrl('email', { workspaceId, id: file }), Object.fromEntries(search));
}
